<template>
  <main-dialog
    :show="uploadModal.show"
    title="New Document"
    subtitle="Create a new document template"
    max-width="440px"
    @close="uploadModal.show = false"
  >
    <div class="ndm-form">
      <div class="ndm-field">
        <label class="ndm-label">Name</label>
        <input
          type="text"
          class="ndm-input"
          v-model="uploadModal.name"
          placeholder="Enter document name…"
          @keydown.enter.prevent="upload"
          ref="nameInput"
        />
      </div>

      <div class="ndm-path" v-if="uploadModal.name">
        <i class="fa fa-folder-open"></i>
        <span>{{ displayPath }}</span>
      </div>
    </div>

    <template #footer>
      <button class="ndm-btn ndm-btn--cancel" @click="uploadModal.show = false">Cancel</button>
      <button
        class="ndm-btn ndm-btn--create"
        @click="upload"
        :disabled="!uploadModal.name || uploadModal.name.trim() === ''"
      >
        <i class="fa fa-plus"></i>
        <span>Create</span>
      </button>
    </template>
  </main-dialog>
</template>

<script>
import MainDialog from "@/components/MainDialog.vue";

export default {
  name: "NewDocumentModal",
  components: { MainDialog },
  props: {
    uploadModal: { type: Object, required: true },
    currentDir: { type: String, required: true },
    currentLib: { type: Object, required: false },
  },
  computed: {
    displayPath() {
      const dir = this.currentDir || "";
      const name = this.uploadModal.name || "";
      return `${dir}/${name}`.replace(/^\//, "");
    },
  },
  watch: {
    "uploadModal.show"(val) {
      if (val) {
        this.$nextTick(() => {
          if (this.$refs.nameInput) this.$refs.nameInput.focus();
        });
      }
    },
  },
  methods: {
    upload() {
      if (!this.uploadModal.name || this.uploadModal.name.trim() === "") return;

      const popup = this.$loadingPopup();
      const data = {
        path: `${this.currentDir}/${this.uploadModal.name}`.replace(/^\//, ""),
      };

      this.$store
        .dispatch("documents/createDocument", data)
        .catch((error) => {
          this.$debugLog(error);
          if (error.response) {
            if (error.response.status == 500) {
              this.$serverErrorModal(error);
              return;
            }
            this.$mainDialog.msgBoxOk(error.response.data.message);
          }
        })
        .finally(() => {
          this.uploadModal.show = false;
          this.uploadModal.name = "";
          popup.hide();
        });
    },
  },
};
</script>

<style scoped lang="scss">
.ndm-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.ndm-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ndm-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
}

.ndm-input {
  width: 100%;
  height: 42px;
  padding: 0 14px;
  background: var(--form-bg, #383a40);
  border: 1px solid var(--form-border, #4e5058);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 14px;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;

  &::placeholder { color: var(--text-muted); }
  &:focus {
    border-color: var(--primary-start);
    box-shadow: 0 0 0 3px rgba(91, 124, 246, 0.12);
  }
}

.ndm-path {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: rgba(91, 124, 246, 0.06);
  border: 1px solid rgba(91, 124, 246, 0.15);
  border-radius: 8px;
  font-size: 13px;
  color: var(--text-secondary);
  font-family: 'SFMono-Regular', 'Consolas', monospace;

  i {
    color: var(--primary-start);
    font-size: 12px;
  }
}

.ndm-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  outline: none;
  transition: all 0.18s ease;

  &--cancel {
    background: transparent;
    border: 1px solid var(--border-hover, #4e5058);
    color: var(--text-secondary);

    &:hover {
      background: var(--bg-card-secondary);
      color: var(--text-primary);
    }
  }

  &--create {
    background: var(--primary-start);
    color: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);

    &:hover {
      opacity: 0.88;
      transform: translateY(-1px);
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.4);
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  }
}
</style>
